// web/themes/custom/ignition_theme/src/js/utils/gradient.js

class GradientController {
    constructor() {
        this.interBubble = null;
        this.mainElement = null;
        this.gradientElement = null;
        this.isInitialized = false;
        this.isOnlyBottom = false; // New property to track only-bottom mode

        // Mouse tracking
        this.curX = 0;
        this.curY = 0;
        this.tgX = 0;
        this.tgY = 0;

        // Scroll tracking
        this.isScrolling = false;
        this.scrollTimeout = null;

        // Throttle scroll events for better performance
        this.throttleDelay = 16; // ~60fps
        this.lastScrollTime = 0;

        this.init();
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    setup() {
        this.interBubble = document.querySelector('.gradient__interactive');
        this.mainElement = document.querySelector('main');
        this.gradientElement = document.querySelector('.gradient');

        if (!this.interBubble || !this.mainElement || !this.gradientElement) {
            return;
        }

        // Check if this is an only-bottom gradient
        this.isOnlyBottom = this.gradientElement.classList.contains('gradient--only-bottom');

        // Read configurable fade distance from CSS custom property
        const computedStyle = getComputedStyle(this.gradientElement);
        this.fadeDistance = parseFloat(computedStyle.getPropertyValue('--gradient-fade-distance')) || 1;

        this.isInitialized = true;
        this.bindEvents();
        this.startAnimation();
        this.updateScrollOpacity(); // Initial opacity calculation
    }

    bindEvents() {
        // Mouse movement with passive listener for better performance
        window.addEventListener('mousemove', this.handleMouseMove.bind(this), { passive: true });

        // Scroll event with throttling
        window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

        // Resize event to recalculate on viewport changes
        window.addEventListener('resize', this.debounce(this.handleResize.bind(this), 250));
    }

    handleMouseMove(event) {
        this.tgX = event.clientX;
        this.tgY = event.clientY;
    }

    handleScroll() {
        const now = performance.now();

        if (now - this.lastScrollTime >= this.throttleDelay) {
            this.updateScrollOpacity();
            this.lastScrollTime = now;
        }

        // Clear existing timeout
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }

        // Set scrolling state
        this.isScrolling = true;

        // Reset scrolling state after scroll ends
        this.scrollTimeout = setTimeout(() => {
            this.isScrolling = false;
        }, 150);
    }

    handleResize() {
        if (this.isInitialized) {
            this.updateScrollOpacity();
        }
    }

    updateScrollOpacity() {
        if (!this.mainElement || !this.gradientElement) return;

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const viewportHeight = window.innerHeight;
        const mainRect = this.mainElement.getBoundingClientRect();
        const mainTop = mainRect.top + scrollTop;
        const mainHeight = this.mainElement.offsetHeight;

        // Calculate scroll position relative to main element
        const scrollInMain = Math.max(0, scrollTop - mainTop);
        const maxScroll = mainHeight - viewportHeight;

        const maxOpacity = document.body.classList.contains('page-node-type-page') ? 0.2 : 0.6;
        let opacity = 0; // Default to 0

        if (this.isOnlyBottom) {
            // Only-bottom mode: only show gradient in the bottom portion of the page
            const fadeInStart = Math.max(0, maxScroll - viewportHeight); // Start fade-in 100vh from bottom
            const fadeInEnd = maxScroll; // Full opacity at the very bottom

            if (scrollInMain >= fadeInStart && scrollInMain <= fadeInEnd) {
                // Fade in from fadeInStart to fadeInEnd
                const fadeInProgress = (scrollInMain - fadeInStart) / viewportHeight;
                opacity = Math.min(maxOpacity, fadeInProgress * maxOpacity);
            } else if (scrollInMain > fadeInEnd) {
                // Full opacity when past the fade-in zone (at the very bottom)
                opacity = maxOpacity;
            }
            // Otherwise opacity remains 0 (for the top and middle portions)
        } else {
            // Normal mode: existing behavior
            opacity = maxOpacity;

            // Fade out based on configurable distance (fadeDistance * viewportHeight)
            const fadeOutDistance = viewportHeight * this.fadeDistance;
            if (scrollInMain <= fadeOutDistance) {
                opacity = Math.max(0, maxOpacity - (scrollInMain / fadeOutDistance * maxOpacity));
            } else {
                opacity = 0;
            }

            // Fade in before reaching 100vh from the end
            // From (mainHeight - 200vh) to (mainHeight - 100vh): opacity goes from 0 to maxOpacity
            const fadeInStart = Math.max(0, maxScroll - viewportHeight);
            const fadeInEnd = maxScroll;

            if (scrollInMain >= fadeInStart && scrollInMain <= fadeInEnd) {
                const fadeInProgress = (scrollInMain - fadeInStart) / viewportHeight;
                const fadeInOpacity = Math.min(maxOpacity, fadeInProgress * maxOpacity);
                opacity = Math.max(opacity, fadeInOpacity);
            } else if (scrollInMain > fadeInEnd) {
                opacity = maxOpacity;
            }
        }

        // Clamp opacity between 0 and maxOpacity
        opacity = Math.max(0, Math.min(maxOpacity, opacity));

        // Firefox fallback: since fixed elements escape edge clipping, it uses absolute position.
        // We push it down via transform to simulate 'fixed' behavior within `<main>`.
        if (window.getComputedStyle(this.gradientElement).position === 'absolute') {
            this.gradientElement.style.transform = `translate3d(0, ${scrollInMain}px, 0)`;
        } else {
            this.gradientElement.style.transform = '';
        }

        // Apply opacity - for only-bottom mode, set both the CSS custom property and inline style
        if (this.isOnlyBottom) {
            // Override the CSS --gradient-opacity: 0 from the stylesheet
            this.gradientElement.style.opacity = opacity.toString();
        } else {
            // Use CSS custom property for normal mode
            this.gradientElement.style.setProperty('--gradient-opacity', opacity.toString());
        }
    }

    move() {
        if (!this.isInitialized || !this.interBubble) return;

        // Smooth mouse following with easing
        this.curX += (this.tgX - this.curX) / 20;
        this.curY += (this.tgY - this.curY) / 20;

        // Use transform3d for hardware acceleration
        this.interBubble.style.transform = `translate3d(${Math.round(this.curX)}px, ${Math.round(this.curY)}px, 0)`;

        requestAnimationFrame(() => this.move());
    }

    startAnimation() {
        this.move();
    }

    // Utility function for debouncing
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    // Public method to destroy the instance if needed
    destroy() {
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);

        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
        }

        this.isInitialized = false;
    }
}

// Initialize the gradient controller
const gradientController = new GradientController();

// Export for potential external use
window.GradientController = GradientController;
